/**
 * Ganancia real (spec 16) en el Panel general de la dueña, rediseñada el 2026-11-01 a pedido del usuario: la cuenta en
 * escalera (ventas − costo de lo vendido = margen bruto − gastos = ganancia real), cada paso con su % de las ventas y una
 * barra, períodos rápidos, y el aviso de lo vendido sin costo cargado (costo 0: la ganancia sale más alta de lo real).
 * Solo muestra: las cifras son las de `GET /costos/ganancia-real` (`costos/casos.ts`, `gananciaReal`), que no cambian.
 */
import { Money, type GananciaRealDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';

/** «$ 1.234,56»; en negativo, «−$ 75,01» (como los pasos que restan). */
const dolares = (v: string | Money) => {
  const m = typeof v === 'string' ? Money.parse(v) : v;
  return m.esNegativo() ? `−$ ${m.abs().toDisplay()}` : `$ ${m.toDisplay()}`;
};
/** Porcentaje de `parte` sobre `total` (0 si no hay total), para mostrar. */
const pct = (parte: Money, total: Money) => (total.esPositivo() ? parte.div(total).mul('100') : Money.cero());
const fechaCaracas = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(d);
const diasAntes = (fecha: string, n: number) => fechaCaracas(new Date(Date.parse(`${fecha}T12:00:00-04:00`) - n * 86_400_000));

type Periodo = 'hoy' | 'semana' | 'mes' | 'mes_pasado';
/** Rango de cada botón rápido, en días de Caracas. */
export function rangoDe(p: Periodo, hoy: string): { desde: string; hasta: string } {
  if (p === 'hoy') return { desde: hoy, hasta: hoy };
  if (p === 'semana') return { desde: diasAntes(hoy, 6), hasta: hoy };
  if (p === 'mes') return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
  const finPasado = diasAntes(`${hoy.slice(0, 7)}-01`, 1);
  return { desde: `${finPasado.slice(0, 7)}-01`, hasta: finPasado };
}
const PERIODOS: [Periodo, string][] = [['hoy', 'Hoy'], ['semana', 'Últimos 7 días'], ['mes', 'Este mes'], ['mes_pasado', 'Mes pasado']];

function Paso({ signo, etiqueta, valor, porcentaje, barra, fuerte, testid, nota }: { signo: '' | '−' | '='; etiqueta: string; valor: Money; porcentaje: Money; barra: string; fuerte?: boolean; testid?: string; nota?: string }) {
  const ancho = Math.max(0, Math.min(100, Number(porcentaje.abs().toDocumento())));
  return (
    <div className={`space-y-1 ${signo === '=' ? 'border-t pt-2' : ''}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className={fuerte ? 'font-semibold' : ''}><span className="inline-block w-4 text-muted-foreground">{signo}</span>{etiqueta}</span>
        <span className="text-right">
          <b className={`num ${fuerte ? 'text-lg' : ''} ${valor.esNegativo() ? 'text-destructive' : ''}`} data-testid={testid}>{signo === '−' ? '−' : ''}{dolares(valor)}</b>
          <span className="ml-2 inline-block w-16 text-xs text-muted-foreground num">{porcentaje.toDisplay()} %</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted"><div className={`h-1.5 rounded-full ${barra}`} style={{ width: `${ancho}%` }} /></div>
      {nota ? <p className="text-[11px] text-muted-foreground">{nota}</p> : null}
    </div>
  );
}

export function GananciaReal() {
  const { usuario } = useAuth();
  const hoy = fechaCaracas(new Date());
  const [periodo, setPeriodo] = useState<Periodo | null>('mes');
  const [rango, setRango] = useState(rangoDe('mes', hoy));
  const [ambito, setAmbito] = useState<'todos' | 'tienda' | 'no_tienda'>('todos');
  const g = useQuery({ queryKey: ['costos', 'ganancia-real', rango, ambito], queryFn: () => get<GananciaRealDTO>('/costos/ganancia-real', { ...rango, ambito }) });
  const elegir = (p: Periodo) => { setPeriodo(p); setRango(rangoDe(p, hoy)); };
  const d = g.data;
  const ventas = Money.parse(d?.ventas ?? '0');
  const costo = Money.parse(d?.costo_vendido ?? '0');
  const margen = Money.parse(d?.margen_bruto ?? '0');
  const gSin = Money.parse(d?.gastos_sin_factura ?? '0');
  const gCon = Money.parse(d?.gastos_con_factura ?? '0');
  const ganancia = Money.parse(d?.ganancia_real ?? '0');
  const sinCosto = Money.parse(d?.ventas_sin_costo ?? '0');
  const conCosto = ventas.sub(sinCosto);
  return (
    <Card data-testid="ganancia-real">
      <CardHeader className="space-y-3">
        <div>
          <CardTitle>Ganancia real</CardTitle>
          <CardDescription>Lo que queda de lo vendido después de su costo y de los gastos. Cuenta interna, no fiscal; en dólares.</CardDescription>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border" role="group" aria-label="Período">
            {PERIODOS.map(([p, texto]) => (
              <button key={p} type="button" aria-pressed={periodo === p} className={`px-3 py-1.5 text-sm font-medium ${periodo === p ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => elegir(p)} data-testid={`ganancia-periodo-${p}`}>{texto}</button>
            ))}
          </div>
          <Campo etiqueta="Desde" htmlFor="gr-desde"><Input id="gr-desde" type="date" value={rango.desde} onChange={(e) => { setPeriodo(null); setRango((r) => ({ ...r, desde: e.target.value })); }} className="w-40" data-testid="ganancia-desde" /></Campo>
          <Campo etiqueta="Hasta" htmlFor="gr-hasta"><Input id="gr-hasta" type="date" value={rango.hasta} onChange={(e) => { setPeriodo(null); setRango((r) => ({ ...r, hasta: e.target.value })); }} className="w-40" data-testid="ganancia-hasta" /></Campo>
          {usuario?.rol === 'superadmin' ? <Campo etiqueta="Gastos" htmlFor="gr-ambito"><Select id="gr-ambito" value={ambito} onChange={(e) => setAmbito(e.target.value as typeof ambito)} className="w-44" data-testid="ganancia-ambito"><option value="todos">Todos</option><option value="tienda">De la tienda</option><option value="no_tienda">No de la tienda</option></Select></Campo> : null}
        </div>
      </CardHeader>
      <CardContent>
        {d ? (
          <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
            <div className="space-y-2.5 text-sm" data-testid="ganancia-escalera">
              <Paso signo="" etiqueta="Ventas" valor={ventas} porcentaje={pct(ventas, ventas)} barra="bg-mg-verde/70" testid="ganancia-ventas" />
              <Paso signo="−" etiqueta="Costo de lo vendido" valor={costo} porcentaje={pct(costo, ventas)} barra="bg-red-300" testid="ganancia-costo" />
              <Paso signo="=" etiqueta="Margen bruto" valor={margen} porcentaje={pct(margen, ventas)} barra="bg-mg-verde/50" fuerte testid="ganancia-margen" />
              <Paso signo="−" etiqueta="Gastos generales sin factura" valor={gSin} porcentaje={pct(gSin, ventas)} barra="bg-amber-300" testid="ganancia-sin-factura" />
              <Paso signo="−" etiqueta="Gastos operativos con factura" valor={gCon} porcentaje={pct(gCon, ventas)} barra="bg-amber-300" testid="ganancia-con-factura" nota="Los gastos repartidos a productos ya van dentro del costo de lo vendido." />
              <Paso signo="=" etiqueta="Ganancia real" valor={ganancia} porcentaje={pct(ganancia, ventas)} barra={ganancia.esNegativo() ? 'bg-destructive' : 'bg-mg-verde'} fuerte />
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-muted/60 p-4">
                <p className="text-xs text-muted-foreground">Ganancia real · {rango.desde === rango.hasta ? rango.desde : `${rango.desde} al ${rango.hasta}`}</p>
                <p className={`num text-3xl font-semibold ${ganancia.esNegativo() ? 'text-destructive' : 'text-mg-verde-oscuro'}`} data-testid="ganancia-real-valor">{dolares(ganancia)}</p>
                <p className="text-sm text-muted-foreground" data-testid="ganancia-por-dolar">{!ventas.esPositivo() ? 'Sin ventas en el período.' : ganancia.esNegativo() ? `De cada $ 100 vendidos se pierden $ ${pct(ganancia, ventas).abs().toDisplay()}.` : `De cada $ 100 vendidos quedan $ ${pct(ganancia, ventas).toDisplay()}.`}</p>
              </div>
              {sinCosto.esPositivo() ? (
                <div className="space-y-1 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" role="status" data-testid="ganancia-sin-costo">
                  <p className="font-semibold"><AlertTriangle className="mr-1 inline h-4 w-4" />Hay ventas sin costo cargado</p>
                  <p>{dolares(sinCosto)} de las ventas ({pct(sinCosto, ventas).toDisplay()} %) son de {d.productos_sin_costo} producto{d.productos_sin_costo === 1 ? '' : 's'} que no tenían costo: su costo cuenta como $ 0, así que el costo de lo vendido sale más bajo y la ganancia más alta de lo real.</p>
                  <p>El costo de un producto entra con su primera compra.</p>
                  {conCosto.esPositivo() ? <p data-testid="ganancia-referencia">Referencia: lo vendido que sí tiene costo ({dolares(conCosto)}) dejó un margen bruto de {pct(conCosto.sub(costo), conCosto).toDisplay()} %.</p> : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : <p className="text-xs text-muted-foreground">{g.isError ? mensajeDeError(g.error) : 'Calculando…'}</p>}
      </CardContent>
    </Card>
  );
}
